// Copyright (c) 2020 by Michael Marucheck
//
// Use of this source code is governed by an MIT-style license that
// can be found in the LICENSE file distributed with this file.

import React, { useState, useEffect } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import "react-day-picker/lib/style.css";

import { ReviewState, ActivityReview } from "../store/schema";
import { getApi, coordCache } from "../store/api";
import { nameOf } from "../store/ApiUtil";
import {
  encouragement,
  lazy,
  logIfErrorA,
  durationString,
  createOuts,
  capitalizeFirstLetter,
} from "../util";
import {
  useAsyncGuard,
  update,
  useWidthGate,
  Lifecycle,
  observe,
} from "../UiUtil";
import { URLS, breadcrumbs } from "../urls";
import { NavBar, navLink } from "../components/NavBar";
import { SaveButton } from "../components/SaveButton";
import { PageContainer } from "../components/PageContainer";
import { CancelButton } from "../components/CancelButton";
import { Breadcrumbs } from "../components/BreadCrumb";
import { ReviewStateChooser } from "../components/ReviewStateChooser";
import { logCrash } from "../store/crash";

const { debug } = createOuts("ActivityReviewPage", true);

const WIDTH_GATE = {
  wideNav: 501,
};

const Page: React.FC<RouteComponentProps<{ uid: string }>> = ({
  match,
}) => {
  debug("======= ActivityReviewPage =======");
  const uid = match.params.uid;
  const widthBucket = useWidthGate(WIDTH_GATE);
  const [state, setState] = useState(coordCache().get());
  const [lifecycle, setLifecycle] = useState(Lifecycle.VIEWED);
  const edited = () => setLifecycle(Lifecycle.EDITED);

  const {
    user,
    loaded,
    school,
    actIndex,
    actRvwIndex,
    catIndex,
    orgIndex,
    volIndex,
  } = state;
  const activity = actIndex.get(uid);
  const review = actRvwIndex.get(uid);

  const [formUid, setFormUid] = useState(uid);

  const initReviewState = (review && review.state) || ReviewState.Pending;
  const [reviewState, setReviewState] = observe(edited, useState(initReviewState));
  const [originalReviewState] = useState(initReviewState);
  debug(`reviewState = ${reviewState}`);

  const startDate = (activity && activity.startDate) || "";
  const endDate = (activity && activity.endDate) || "";
  const duration = (activity && durationString(activity.durationInSec)) || "";
  const description = (activity && activity.description) || "";
  const category = nameOf(activity && activity.categoryUid, catIndex) || "";
  const organization = nameOf(activity && activity.organizationUid, orgIndex) || "";
  const volunteer = nameOf(activity && activity.volunteerUid, volIndex) || "";

  if (uid !== formUid) {
    setFormUid(uid);
    setLifecycle(Lifecycle.VIEWED);
    initializeForm(review || emptyReview());
  }

  const api = getApi();
  const guard = useAsyncGuard();
  const concerns = {}

  useEffect(() => {
    return coordCache().subscribe(newState => {
      if (update("setState", state, setState, newState)) {
        if (lifecycle !== Lifecycle.EDITED) {
          const doc = newState.activityReviews.find(x => x.data.uid === uid);
          const newReview = doc == null ? emptyReview() : doc.data;
          initializeForm(newReview);
          debug(`state changed, updated form to match new state`);
        } else {
          debug(`state changed but form is edited, ignoring update`);
        }
      } else {
        debug(`volCache notified of update but nothing changed`);
      }
    });
  });

  function emptyReview(): ActivityReview {
    return {
      uid,
      schoolUid: activity == null ? "" : activity.schoolUid,
      state: ReviewState.Pending,
      contents: activity,
    };
  }

  function initializeForm(review: ActivityReview) {
    update("reviewState", reviewState, setReviewState, review.state);
    setLifecycle(Lifecycle.VIEWED);
  }

  const parseForm = lazy<ActivityReview>(() => {
    return {
      uid,
      schoolUid: activity ? activity.schoolUid : "",
      state: reviewState,
      contents: activity,
    };
  });

  async function save() {
    debug("called 'save'");
    const review = parseForm();
    debug(`Saving ${uid}`, review)
    await saveActivityReview(review);
  }

  async function saveActivityReview(review: ActivityReview) {
    await logIfErrorA(() => guard(api.coordinator.saveActivityReview(review)));
  }

  const crumbs = (() => {
    switch (reviewState) {
      case ReviewState.Approved: return breadcrumbs().cActivitiesApproved;
      case ReviewState.Pending: return breadcrumbs().cActivitiesPending;
      case ReviewState.Rejected: return breadcrumbs().cActivitiesRejected;
      default:
        logCrash(new Error(`ActivityReviewPage.crumbs: unexpected status: ${reviewState}`));
        return breadcrumbs().cHome;
    }
  })();

  const canReview = activity != null;
  const title = canReview ? "Review Activity" : "Activity Not Found";

  return (
    <PageContainer
      title={title}
      crumbs={crumbs}
      administrators={state.administrators}
      isCoordinator={true}
      school={state.school}
      checks={{ user, loaded }}
    >
      <>
        <div className="details-form">
          <div className="details">
            <div className="detail">
              <label htmlFor="volunteer">Volunteer:</label>
              <input id="volunteer" type="text" value={volunteer} disabled={true} />
            </div>
            <div className="detail">
              <label htmlFor="start-date">Start Date:</label>
              <input id="start-date" type="text" value={startDate} disabled={true} />
            </div>
            <div className="detail">
              <label htmlFor="end-date">End Date:</label>
              <input id="end-date" type="text" value={endDate} disabled={true} />
            </div>
            <div className="detail">
              <label htmlFor="duration">Duration:</label>
              <input id="duration" type="text" value={duration} disabled={true} />
            </div>
            <div className="detail">
              <label htmlFor="category">Category:</label>
              <input id="category" type="text" value={category} disabled={true} />
            </div>
            <div className="detail">
              <label htmlFor="organization">Organization:</label>
              <input id="organization" type="text" value={organization} disabled={true} />
            </div>
            <div className="detail">
              <label htmlFor="description">Description:</label>
              <textarea id="description" value={description} rows={3} disabled={true} />
            </div>
            <div className="detail">
              <label htmlFor="school">School:</label>
              <input
                id="school"
                type="text"
                value={school == null ? "Loading.." : school.data.name}
                disabled={true}
              />
            </div>
            <div className="detail">
              <label htmlFor="review">Review Status</label>
              <ReviewStateChooser
                htmlId="review"
                reviewState={reviewState}
                onChange={setReviewState}
                disabled={!canReview} />
            </div>
          </div>
          <div className="done">
            <SaveButton
              lifecycle={lifecycle}
              setLifecycle={setLifecycle}
              concerns={concerns}
              onSave={save}
            />
            <CancelButton
              lifecycle={lifecycle}
              setLifecycle={setLifecycle}
              onCancel={() => initializeForm(review || emptyReview())}
            />
          </div>
          {lifecycle === Lifecycle.SAVED && (
            <>
              <div className="nav">
                {encouragement()} Here are some ideas for where to go next:
                </div>
              <NavBar
                wide={widthBucket >= WIDTH_GATE.wideNav}
                links={[
                  navLink(URLS.cHome(), "home"),
                  navLink(
                    URLS.cActivities(originalReviewState),
                    `${capitalizeFirstLetter(originalReviewState)} Activities`
                  ),
                ]}
              />
            </>
          )}
        </div>
        {lifecycle === Lifecycle.VIEWED && (
          <>
            <div className="section"></div>
            <Breadcrumbs crumbs={crumbs} />
          </>
        )}
      </>
    </PageContainer>
  );
};
export const ActivityReviewPage = withRouter(Page);
